import React from 'react';

type StatsCardProps = {
  title: string;
  value: number | string;
  description: string;
  icon: React.ReactNode;
  variant?: 'default' | 'warning' | 'success';
};

const variantStyles = {
  default: {
    bg: 'bg-white',
    iconBg: 'bg-blue-50',
    iconText: 'text-blue-600',
    valueBg: '',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    valueBg: 'text-amber-700',
  },
  success: {
    bg: 'bg-emerald-50 border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    valueBg: 'text-emerald-700',
  },
};

export const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  description,
  icon,
  variant = 'default',
}) => {
  const styles = variantStyles[variant];

  return (
    <div
      className={`${styles.bg} rounded-xl border border-gray-200 p-5 transition-all hover:shadow-md`}
      role="status"
      aria-label={`${title}: ${value}`}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            {title}
          </p>
          <p className={`text-2xl font-bold ${styles.valueBg || 'text-gray-900'}`}>
            {value}
          </p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        <div className={`${styles.iconBg} ${styles.iconText} p-2.5 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

type RegionCardProps = {
  name: string;
  count: number;
  total: number;
  color: {
    bg: string;
    border: string;
    iconBg: string;
    iconText: string;
    barBg: string;
    barFill: string;
    valueText: string;
  };
  icon: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
};

export const RegionCard: React.FC<RegionCardProps> = ({
  name,
  count,
  total,
  color,
  icon,
  active = false,
  onClick,
}) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;

  return (
    <div
      className={`${color.bg} ${color.border} rounded-xl border p-4 transition-all hover:shadow-md cursor-pointer select-none ${
        active ? 'ring-2 ring-offset-1 ring-blue-500 shadow-md' : ''
      }`}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={`${name}: ${count}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`${color.iconBg} ${color.iconText} p-2 rounded-lg`}>
          {icon}
        </div>
        <span className={`text-2xl font-bold ${color.valueText}`}>{count}</span>
      </div>
      <p className="text-sm font-semibold text-gray-800 mb-1 truncate" title={name}>
        {name}
      </p>
      <div className={`w-full ${color.barBg} rounded-full h-1.5 mb-1.5`}>
        <div
          className={`${color.barFill} h-1.5 rounded-full transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-gray-500">{pct}% do total</p>
    </div>
  );
};

export const REGION_COLORS: Record<string, RegionCardProps['color']> = {
  'Sul': {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    iconBg: 'bg-sky-100',
    iconText: 'text-sky-600',
    barBg: 'bg-sky-100',
    barFill: 'bg-sky-500',
    valueText: 'text-sky-700',
  },
  'São Paulo': {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-600',
    barBg: 'bg-emerald-100',
    barFill: 'bg-emerald-500',
    valueText: 'text-emerald-700',
  },
  'Centro-Norte': {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    barBg: 'bg-amber-100',
    barFill: 'bg-amber-500',
    valueText: 'text-amber-700',
  },
  'Leste': {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconText: 'text-rose-600',
    barBg: 'bg-rose-100',
    barFill: 'bg-rose-500',
    valueText: 'text-rose-700',
  },
  'Nordeste': {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    iconBg: 'bg-teal-100',
    iconText: 'text-teal-600',
    barBg: 'bg-teal-100',
    barFill: 'bg-teal-500',
    valueText: 'text-teal-700',
  },
  'Não definido': {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    iconBg: 'bg-gray-100',
    iconText: 'text-gray-500',
    barBg: 'bg-gray-100',
    barFill: 'bg-gray-400',
    valueText: 'text-gray-600',
  },
};
